import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { CalendarIcon, Plus, Loader2, Clock, Edit2, Ban, Copy, ShoppingBag } from 'lucide-react';
import { ProducerLayout } from '@/components/producer';
import { useProducerById } from '@/hooks/useMyProducers';
import {
  usePickupWindows,
  useCreatePickupWindow,
  useUpdatePickupWindowStatus,
  useUpdatePickupWindow,
  useWindowOrderCounts,
  formatPickupWindowLabel,
  type PickupWindow,
  type PickupWindowInput,
  type PickupWindowUpdateInput,
} from '@/hooks/usePickupWindows';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const STATUS_LABELS: Record<string, string> = {
  active: 'Aktiv',
  closed: 'Inaktiv',
  cancelled: 'Avbruten',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  closed: 'bg-muted text-muted-foreground',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export default function ProducerPickupWindowsPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { data: windows = [], isLoading } = usePickupWindows(farmId);
  const createWindow = useCreatePickupWindow();
  const updateStatus = useUpdatePickupWindowStatus();
  const updateWindow = useUpdatePickupWindow();
  const { toast } = useToast();

  // Order counts for all windows
  const windowIds = useMemo(() => windows.map(w => w.id), [windows]);
  const { data: orderCounts = [] } = useWindowOrderCounts(windowIds);
  const orderCountMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const oc of orderCounts) m.set(oc.pickup_window_id, oc.order_count);
    return m;
  }, [orderCounts]);

  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingWindow, setEditingWindow] = useState<PickupWindow | null>(null);
  const [inactivateTarget, setInactivateTarget] = useState<PickupWindow | null>(null);

  // Form state
  const [pickupDate, setPickupDate] = useState<Date>();
  const [pickupStart, setPickupStart] = useState('10:00');
  const [pickupEnd, setPickupEnd] = useState('14:00');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  const [note, setNote] = useState('');
  const [pickupInstructions, setPickupInstructions] = useState('');

  const resetForm = () => {
    setPickupDate(undefined);
    setPickupStart('10:00');
    setPickupEnd('14:00');
    setDeadlineDate('');
    setDeadlineTime('');
    setNote('');
    setPickupInstructions('');
    setEditingWindow(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogMode('create');
  };

  const openEdit = (w: PickupWindow) => {
    setEditingWindow(w);
    setPickupDate(new Date(w.pickup_date + 'T00:00:00'));
    setPickupStart(w.pickup_start.slice(0, 5));
    setPickupEnd(w.pickup_end.slice(0, 5));
    if (w.order_deadline_at) {
      const dt = new Date(w.order_deadline_at);
      setDeadlineDate(format(dt, 'yyyy-MM-dd'));
      setDeadlineTime(format(dt, 'HH:mm'));
    } else {
      setDeadlineDate('');
      setDeadlineTime('');
    }
    setNote(w.note || '');
    setPickupInstructions(w.pickup_instructions || '');
    setDialogMode('edit');
  };

  const openDuplicate = (w: PickupWindow) => {
    setEditingWindow(null);
    setPickupDate(undefined); // User picks new date
    setPickupStart(w.pickup_start.slice(0, 5));
    setPickupEnd(w.pickup_end.slice(0, 5));
    setDeadlineDate('');
    setDeadlineTime('');
    setNote(w.note || '');
    setPickupInstructions(w.pickup_instructions || '');
    setDialogMode('create');
  };

  const buildDeadline = (): string | null => {
    if (deadlineDate && deadlineTime) return `${deadlineDate}T${deadlineTime}:00`;
    if (deadlineDate) return `${deadlineDate}T23:59:00`;
    return null;
  };

  const handleCreate = async () => {
    if (!farmId || !pickupDate) return;
    try {
      await createWindow.mutateAsync({
        producer_id: farmId,
        pickup_date: format(pickupDate, 'yyyy-MM-dd'),
        pickup_start: pickupStart,
        pickup_end: pickupEnd,
        order_deadline_at: buildDeadline(),
        note: note.trim() || null,
        pickup_instructions: pickupInstructions.trim() || null,
      });
      toast({ title: 'Upphämtningsfönster skapat' });
      setDialogMode(null);
      resetForm();
    } catch (err: any) {
      toast({ title: 'Kunde inte skapa', description: err.message, variant: 'destructive' });
    }
  };

  const handleUpdate = async () => {
    if (!farmId || !editingWindow || !pickupDate) return;
    const hasOrders = (orderCountMap.get(editingWindow.id) || 0) > 0;
    try {
      const input: PickupWindowUpdateInput = {
        id: editingWindow.id,
        producer_id: farmId,
        order_deadline_at: buildDeadline(),
        note: note.trim() || null,
        pickup_instructions: pickupInstructions.trim() || null,
      };
      // Only include structural fields if no orders (server also validates)
      if (!hasOrders) {
        input.pickup_date = format(pickupDate, 'yyyy-MM-dd');
        input.pickup_start = pickupStart;
        input.pickup_end = pickupEnd;
      }
      await updateWindow.mutateAsync(input);
      toast({ title: 'Upphämtningsfönster uppdaterat' });
      setDialogMode(null);
      resetForm();
    } catch (err: any) {
      toast({ title: 'Kunde inte uppdatera', description: err.message, variant: 'destructive' });
    }
  };

  const handleInactivate = async () => {
    if (!farmId || !inactivateTarget) return;
    try {
      await updateStatus.mutateAsync({ id: inactivateTarget.id, status: 'closed', producerId: farmId });
      toast({ title: 'Fönstret har inaktiverats' });
      setInactivateTarget(null);
    } catch (err: any) {
      toast({ title: 'Kunde inte inaktivera', description: err.message, variant: 'destructive' });
    }
  };

  const handleReactivate = async (w: PickupWindow) => {
    if (!farmId) return;
    try {
      await updateStatus.mutateAsync({ id: w.id, status: 'active', producerId: farmId });
      toast({ title: 'Fönstret har aktiverats' });
    } catch (err: any) {
      toast({ title: 'Kunde inte aktivera', description: err.message, variant: 'destructive' });
    }
  };

  const now = new Date();
  const upcoming = windows.filter(w => w.status === 'active' && new Date(w.pickup_date + 'T23:59:59') >= now);
  const past = windows.filter(w => w.status !== 'active' || new Date(w.pickup_date + 'T23:59:59') < now);

  const isEditing = dialogMode === 'edit';
  const editHasOrders = editingWindow ? (orderCountMap.get(editingWindow.id) || 0) > 0 : false;
  const isMutating = createWindow.isPending || updateWindow.isPending;

  // --- Client-side validation ---
  const timeError = pickupStart && pickupEnd && pickupEnd <= pickupStart
    ? 'Sluttid måste vara efter starttid.'
    : null;

  const deadlineStr = buildDeadline();
  const deadlineError = (() => {
    if (!deadlineStr || !pickupDate || !pickupStart) return null;
    const pickupStartTs = new Date(`${format(pickupDate, 'yyyy-MM-dd')}T${pickupStart}:00`);
    const deadlineTs = new Date(deadlineStr);
    if (isNaN(deadlineTs.getTime()) || isNaN(pickupStartTs.getTime())) return null;
    return deadlineTs >= pickupStartTs
      ? 'Sista beställningstid måste vara före upphämtningens starttid.'
      : null;
  })();

  const hasValidationError = !!timeError || !!deadlineError;

  if (producerLoading) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName="Laddar..." title="Upphämtning">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Upphämtning">
      <div className="space-y-6" data-testid="producer-pickup-windows-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Upphämtningsfönster</h1>
            <p className="text-muted-foreground mt-1">
              Skapa tider då kunder kan hämta sina beställningar
            </p>
          </div>
          <Button onClick={openCreate} data-testid="producer-pickup-windows-create">
            <Plus className="h-4 w-4 mr-2" />
            Nytt fönster
          </Button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && windows.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="h-8 w-8 text-muted-foreground mb-3" />
              <h3 className="text-lg font-medium">Inga upphämtningsfönster</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Skapa ett upphämtningsfönster så kan kunder välja när de vill hämta sin beställning.
              </p>
            </CardContent>
          </Card>
        )}

        {upcoming.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">Kommande</h2>
            <div className="space-y-2">
              {upcoming.map(w => (
                <PickupWindowCard
                  key={w.id}
                  window={w}
                  orderCount={orderCountMap.get(w.id) || 0}
                  onEdit={openEdit}
                  onDuplicate={openDuplicate}
                  onInactivate={setInactivateTarget}
                  onReactivate={handleReactivate}
                />
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">Tidigare / Inaktiva</h2>
            <div className="space-y-2">
              {past.map(w => (
                <PickupWindowCard
                  key={w.id}
                  window={w}
                  orderCount={orderCountMap.get(w.id) || 0}
                  onEdit={openEdit}
                  onDuplicate={openDuplicate}
                  onInactivate={setInactivateTarget}
                  onReactivate={handleReactivate}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogMode !== null} onOpenChange={(v) => { if (!v) { setDialogMode(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Redigera upphämtningsfönster' : 'Nytt upphämtningsfönster'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Ändra uppgifterna för detta fönster.'
                : 'Ange datum och tid för upphämtning.'}
            </DialogDescription>
          </DialogHeader>

          {isEditing && editHasOrders && (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
              <ShoppingBag className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Det finns beställningar kopplade till detta fönster. Datum och tider kan inte ändras.</span>
            </div>
          )}

          <div className="space-y-4 py-2">
            {/* Date picker */}
            <div className="space-y-1.5">
              <Label>Datum *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={isEditing && editHasOrders}
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !pickupDate && 'text-muted-foreground',
                    )}
                    data-testid="producer-pickup-window-date-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {pickupDate
                      ? format(pickupDate, 'PPP', { locale: sv })
                      : 'Välj datum'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={pickupDate}
                    onSelect={setPickupDate}
                    disabled={(d) => d < new Date(new Date().toDateString())}
                    className={cn('p-3 pointer-events-auto')}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time range */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="pw-start">Från *</Label>
                <Input
                  id="pw-start"
                  type="time"
                  value={pickupStart}
                  onChange={e => setPickupStart(e.target.value)}
                  disabled={isEditing && editHasOrders}
                  data-testid="producer-pickup-window-start"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw-end">Till *</Label>
                <Input
                  id="pw-end"
                  type="time"
                  value={pickupEnd}
                  onChange={e => setPickupEnd(e.target.value)}
                  disabled={isEditing && editHasOrders}
                  data-testid="producer-pickup-window-end"
                />
              </div>
            </div>
            {timeError && (
              <p className="text-sm text-destructive -mt-2">{timeError}</p>
            )}

            {/* Deadline */}
            <div className="space-y-1.5">
              <Label>Beställ senast <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="date"
                  value={deadlineDate}
                  onChange={e => setDeadlineDate(e.target.value)}
                  placeholder="Datum"
                />
                <Input
                  type="time"
                  value={deadlineTime}
                  onChange={e => setDeadlineTime(e.target.value)}
                  placeholder="Tid"
                />
              </div>
              {deadlineError ? (
                <p className="text-sm text-destructive">{deadlineError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Om satt kan kunder inte beställa efter denna tid.
                </p>
              )}
            </div>

            {/* Pickup instructions */}
            <div className="space-y-1.5">
              <Label>Upphämtningsinstruktioner <span className="text-muted-foreground text-xs">(valfritt, visas för kund)</span></Label>
              <Textarea
                value={pickupInstructions}
                onChange={e => setPickupInstructions(e.target.value)}
                placeholder="T.ex. 'Finns i kyldisken i gårdsbutiken' eller 'Märkt med ditt namn'"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Beskriv var och hur kunden hämtar sin beställning.
              </p>
            </div>

            {/* Note */}
            <div className="space-y-1.5">
              <Label>Intern notering <span className="text-muted-foreground text-xs">(valfritt, syns ej för kund)</span></Label>
              <Textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="T.ex. 'Parkera vid ladan'"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogMode(null); resetForm(); }}>
              Avbryt
            </Button>
            <Button
              onClick={isEditing ? handleUpdate : handleCreate}
              disabled={!pickupDate || !pickupStart || !pickupEnd || isMutating || hasValidationError}
              data-testid="producer-pickup-window-save"
            >
              {isMutating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEditing ? 'Spara' : 'Skapa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inactivate confirm dialog */}
      <AlertDialog open={!!inactivateTarget} onOpenChange={(v) => { if (!v) setInactivateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inaktivera upphämtningsfönster?</AlertDialogTitle>
            <AlertDialogDescription>
              {(inactivateTarget && (orderCountMap.get(inactivateTarget.id) || 0) > 0)
                ? `Det finns ${orderCountMap.get(inactivateTarget.id)} beställning(ar) kopplade. Fönstret döljs för nya beställningar men befintliga påverkas inte.`
                : 'Kunder kommer inte längre att kunna välja detta fönster för nya beställningar.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleInactivate}>Inaktivera</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ProducerLayout>
  );
}

/* ─── Card ─── */

interface PickupWindowCardProps {
  window: PickupWindow;
  orderCount: number;
  onEdit: (w: PickupWindow) => void;
  onDuplicate: (w: PickupWindow) => void;
  onInactivate: (w: PickupWindow) => void;
  onReactivate: (w: PickupWindow) => void;
}

function PickupWindowCard({ window: w, orderCount, onEdit, onDuplicate, onInactivate, onReactivate }: PickupWindowCardProps) {
  const label = formatPickupWindowLabel(w);
  const isActive = w.status === 'active';
  const isPast = new Date(w.pickup_date + 'T23:59:59') < new Date();

  return (
    <Card className={!isActive || isPast ? 'opacity-60' : ''} data-testid={`producer-pickup-window-card-${w.id}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-sm">{label}</p>
              <Badge variant="secondary" className={cn('text-xs', STATUS_COLORS[w.status])}>
                {STATUS_LABELS[w.status] || w.status}
              </Badge>
              {isPast && isActive && (
                <Badge variant="secondary" className="text-xs">Passerat</Badge>
              )}
              {orderCount > 0 && (
                <Badge variant="outline" className="text-xs">
                  {orderCount} best.
                </Badge>
              )}
            </div>
            {w.order_deadline_at && (
              <p className="text-xs text-muted-foreground mt-1">
                Beställ senast: {format(new Date(w.order_deadline_at), 'PPP HH:mm', { locale: sv })}
              </p>
            )}
            {w.pickup_instructions && (
              <p className="text-xs text-muted-foreground mt-0.5">
                📍 {w.pickup_instructions}
              </p>
            )}
            {w.note && (
              <p className="text-xs text-muted-foreground mt-0.5 italic">{w.note}</p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground shrink-0">
                •••
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(w)}>
                <Edit2 className="h-4 w-4 mr-2" />
                Redigera
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(w)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicera
              </DropdownMenuItem>
              {isActive && !isPast ? (
                <DropdownMenuItem onClick={() => onInactivate(w)} className="text-destructive focus:text-destructive">
                  <Ban className="h-4 w-4 mr-2" />
                  Inaktivera
                </DropdownMenuItem>
              ) : w.status === 'closed' && !isPast ? (
                <DropdownMenuItem onClick={() => onReactivate(w)}>
                  <Clock className="h-4 w-4 mr-2" />
                  Aktivera igen
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}
